import { get } from '../index';

// POST-REFACTOR: the legacy GET /analytics route was a kitchen-sink
// fat call (60+ seconds, ~20 MB for admin users, looping every company
// × every user × ~11 Mongo queries × 4 curlight queries) but Dashboard
// only ever read the `user` sub-object. Replaced with 6 focused
// per-user endpoints that fan out in parallel — each is scoped, indexed,
// and fast (~50-500 ms individually). The Promise.all reassembles the
// same shape Dashboard already consumes, so no other code needs to
// change.
//
// Endpoints:
//   /me/summary    -> totalHoursWorked, totalCostIncured, totalHoursPerProject, lastLoginTime
//   /me/projects   -> projectWorked[], totalEstimationsByUser[]
//   /me/health     -> totalHealthCheckReports[]
//   /me/activity   -> activityLogs[] (default 200 most recent)
//   /me/shares     -> totalSharedAccessByUser[], totalPendingAccessByUser[]
//   /me/databases  -> curlight catalog counts {revit, catalogMaster, catalogOmni, catalogUniFormat}
//
// Soft-fail per endpoint: if one fails, the others still return so the
// dashboard renders what it can (e.g., shares unavailable doesn't kill
// the hours-worked tile).
const safeGet = async (url) => {
  try { return await get(url); }
  catch (err) { console.log('Dashboard fetch failed:', url, err?.message); return null; }
};

export const getDashboardData = async () => {
  const [summary, projects, health, activity, shares, databases] = await Promise.all([
    safeGet('/analytics/me/summary'),
    safeGet('/analytics/me/projects'),
    safeGet('/analytics/me/health'),
    safeGet('/analytics/me/activity'),
    safeGet('/analytics/me/shares'),
    safeGet('/analytics/me/databases'),
  ]);

  // Reassemble the legacy { message, data: { user: {...} } } shape
  // Dashboard.jsx already consumes — keeps the call site change small
  // and gives Dashboard a single object to dispatch.
  return {
    message: 'Succeed',
    data: {
      user: {
        // /me/summary
        totalHoursWorked: summary?.totalHoursWorked ?? 0,
        // null for members: the server sends money to admins only.
        totalCostIncured: summary?.totalCostIncured ?? null,
        totalHoursPerProject: summary?.totalHoursPerProject ?? [],
        lastLoginTime: summary?.lastLoginTime ?? null,
        // /me/projects
        projectWorked: projects?.projectWorked ?? [],
        totalEstimationsByUser: projects?.totalEstimationsByUser ?? [],
        // /me/health
        totalHealthCheckReports: health?.totalHealthCheckReports ?? [],
        // /me/activity
        activityLogs: activity?.activityLogs ?? [],
        // /me/shares
        totalSharedAccessByUser: shares?.totalSharedAccessByUser ?? [],
        totalPendingAccessByUser: shares?.totalPendingAccessByUser ?? [],
        // /me/databases (curlight)
        companyDatabases: databases ?? { revit: 0, catalogMaster: 0, catalogOmni: 0, catalogUniFormat: 0 },
      },
      // Admin/companyAdmin platform-wide grids are NOT included. If a
      // future admin grid view needs them, it should call its own
      // dedicated endpoint — keeping it out of this hot path is the
      // whole point of the refactor.
      company: [],
    },
  };
};
